import axios from 'axios';

// Security: Fail fast if API URL is not configured
if (!process.env.REACT_APP_BACKEND_URL) {
  throw new Error('REACT_APP_BACKEND_URL environment variable is not set');
}

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Authenticated API instance
const api = axios.create({ baseURL: `${API_BASE}/api`, timeout: 15000 });

// Public API instance (no auth required)
const publicApi = axios.create({ baseURL: `${API_BASE}/api`, timeout: 15000 });

// Attach token to authenticated requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pp_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Security: Handle 401 responses by clearing token and redirecting to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('pp_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to construct media URLs
export const getMediaUrl = (path) => path ? `${API_BASE}${path}` : '';

// Auth
export const loginApi = (email, password) => api.post('/auth/login', { email, password });
export const signupApi = (name, email, password) => publicApi.post('/auth/signup', { name, email, password });
export const getMeApi = () => api.get('/auth/me');
export const changePasswordApi = (currentPassword, newPassword) => api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
export const getStaffApi = () => api.get('/auth/staff');
export const registerStaffApi = (data) => api.post('/auth/register', data);

// User Management (admin only)
export const getUsersApi = () => api.get('/users');
export const updateUserRoleApi = (userId, role) => api.put(`/users/${userId}/role?role=${role}`);
export const deleteUserApi = (userId) => api.delete(`/users/${userId}`);

// Invite (public)
export const getInviteApi = (token) => publicApi.get(`/invite/${token}`);
export const setInvitePasswordApi = (token, password) => publicApi.post(`/invite/${token}/set-password`, { password });

// Courts
export const getCourtsApi = () => api.get('/courts');
export const createCourtApi = (data) => api.post('/courts', data);
export const updateCourtApi = (id, data) => api.put(`/courts/${id}`, data);

// Bookings
export const getBookingsApi = (date) => api.get('/bookings', { params: { date } });
export const createBookingApi = (data) => api.post('/bookings', data);
export const createPlayerBookingApi = (data) => publicApi.post('/player/bookings', data);
export const updateBookingApi = (id, data) => api.put(`/bookings/${id}`, data);
export const releaseBookingApi = (id) => api.delete(`/bookings/${id}`);
export const markPaidApi = (id) => api.post(`/bookings/${id}/pay`);
export const extendBookingApi = (id, extraHours) => api.post(`/bookings/${id}/extend`, { extra_hours: extraHours });
export const moveBookingApi = (id, courtId) => api.post(`/bookings/${id}/move`, { court_id: courtId });
export const updateBookingNotesApi = (id, notes) => api.put(`/bookings/${id}/notes`, { notes });
// TODO: approveBookingApi and confirmPaymentApi superseded by markProofReceivedApi + verifyAndConfirmApi — remove after audit
export const approveBookingApi = (id) => api.post(`/bookings/${id}/approve`);
export const confirmPaymentApi = (id) => api.post(`/bookings/${id}/confirm-payment`);
export const markProofReceivedApi = (id) => api.post(`/bookings/${id}/mark-proof-received`);
export const verifyAndConfirmApi = (id) => api.post(`/bookings/${id}/verify-confirm`);

// Availability
export const getAvailabilityApi = (date, bookingIntent) => api.get('/availability', { params: { date, booking_intent: bookingIntent } });
export const getNextAvailableApi = () => api.get('/next-available');

// Open Play
export const getOpenPlayApi = (date) => api.get('/open-play', { params: { date } });
export const createOpenPlayApi = (data) => api.post('/open-play', data);
export const joinOpenPlayApi = (sessionId, data) => api.post(`/open-play/${sessionId}/join`, data);
export const removeFromOpenPlayApi = (sessionId, playerId) => api.post(`/open-play/${sessionId}/remove/${playerId}`);
export const checkinPlayerApi = (sessionId, playerId) => api.post(`/open-play/${sessionId}/checkin/${playerId}`);
export const payOpenPlayApi = (sessionId, playerId) => api.post(`/open-play/${sessionId}/pay/${playerId}`);
export const cancelOpenPlayApi = (sessionId) => api.delete(`/open-play/${sessionId}`);
export const updateOpenPlayApi = (sessionId, data) => api.put(`/open-play/${sessionId}`, data);

// Dashboard
export const getTodayDashboardApi = () => api.get('/dashboard/today');
export const getTimelineApi = (date) => api.get('/dashboard/timeline', { params: { date } });
export const getGlobalAlertsApi = (currentDate) => api.get('/bookings/global-alerts', { params: { current_date: currentDate } });

// Facility Rules
export const getFacilityRulesApi = () => api.get('/facility-rules');
export const updateFacilityRulesApi = (data) => api.put('/facility-rules', data);
export const getPublicFacilityRulesApi = () => publicApi.get('/facility-rules');

// Activity Logs
export const getActivityLogsApi = (limit) => api.get('/activity-logs', { params: { limit } });

// Court Blocks
export const getCourtBlocksApi = (date) => api.get('/court-blocks', { params: { date } });
export const createCourtBlockApi = (data) => api.post('/court-blocks', data);
export const removeCourtBlockApi = (id) => api.delete(`/court-blocks/${id}`);

// Recurring Blocks
export const getRecurringBlocksApi = () => api.get('/recurring-blocks');
export const createRecurringBlockApi = (data) => api.post('/recurring-blocks', data);
export const updateRecurringBlockApi = (id, data) => api.put(`/recurring-blocks/${id}`, data);
export const regenerateRecurringBlockApi = (id) => api.post(`/recurring-blocks/${id}/regenerate`);
export const deleteRecurringSeriesApi = (id) => api.delete(`/recurring-blocks/${id}`);
export const excludeDateFromSeriesApi = (id, date) => api.post(`/recurring-blocks/${id}/exclude/${date}`);

// Recurring Bookings
export const getRecurringBookingsApi = () => api.get('/recurring-bookings');
export const createRecurringBookingApi = (data) => api.post('/recurring-bookings', data);
export const updateRecurringBookingApi = (id, data) => api.put(`/recurring-bookings/${id}`, data);
export const regenerateRecurringBookingApi = (id) => api.post(`/recurring-bookings/${id}/regenerate`);
export const deleteRecurringBookingSeriesApi = (id) => api.delete(`/recurring-bookings/${id}`);
export const excludeDateFromBookingSeriesApi = (id, date) => api.post(`/recurring-bookings/${id}/exclude/${date}`);

// Public Open Play (no auth required)
export const getOpenPlayPublicApi = (sessionId) => publicApi.get(`/open-play/${sessionId}/public`);
export const rsvpOpenPlayApi = (sessionId, data) => publicApi.post(`/open-play/${sessionId}/rsvp`, data);
export const cancelRsvpApi = (sessionId, playerId) => publicApi.delete(`/open-play/${sessionId}/rsvp/${playerId}`);
export const uploadOpenPlayPaymentApi = (sessionId, playerId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return publicApi.post(`/open-play/${sessionId}/payment/${playerId}`, formData);
};
export const getOpenPlayPaymentStatusApi = (sessionId, playerId) => publicApi.get(`/open-play/${sessionId}/payment-status/${playerId}`);

// OTP (public)
export const sendOtpApi = (email) => publicApi.post('/otp/send', { email });
export const verifyOtpApi = (email, otp) => publicApi.post('/otp/verify', { email, otp });

// Payment Proof Upload (public - player uploads)
export const startProofUploadApi = (bookingId) => publicApi.post(`/bookings/${bookingId}/proof/start`);
export const uploadPaymentProofApi = (bookingId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return publicApi.post(`/bookings/${bookingId}/upload-proof`, formData);
};
export const submitReceiptTextApi = (bookingId, receiptText) =>
  publicApi.post(`/bookings/${bookingId}/receipt`, { receipt_text: receiptText });
export const getBookingApi = (bookingId) => publicApi.get(`/bookings/${bookingId}`);

// Booking Approval (operator only)
export const rejectBookingApi = (bookingId) => api.post(`/bookings/${bookingId}/reject`);

// Coaching Sessions
export const createCoachingSessionApi = (data) => api.post('/coaching-sessions', data);
export const getCoachingSessionsApi = () => api.get('/coaching-sessions');
export const renameCoachingSessionApi = (id, coach_name) => api.patch(`/coaching-sessions/${id}`, { coach_name });
export const deleteCoachingSessionApi = (id) => api.delete(`/coaching-sessions/${id}`);
export const getCoachingSessionPublicApi = (code) => publicApi.get(`/coaching/${code}`);
export const getCoachingAvailabilityApi = (code, date) => publicApi.get(`/coaching/${code}/availability?date=${date}`);
export const createCoachingBookingApi = (code, data) => publicApi.post(`/coaching/${code}/book`, data);

// Recurring Bookings Cleanup
export const cleanupOrphanedRecurringApi = () => api.post('/recurring-bookings/cleanup-orphaned');

// Tournaments
export const createTournamentApi = (data) => api.post('/tournaments', data);
export const getTournamentsApi = () => api.get('/tournaments');
export const updateTournamentApi = (id, data) => api.put(`/tournaments/${id}`, data);
export const cancelTournamentApi = (id) => api.delete(`/tournaments/${id}`);

// Facebook auto-post
export const getFacebookPreviewApi = () => api.get('/facebook/preview');
export const postFacebookNowApi = () => api.post('/facebook/post-now');
export const getFacebookHistoryApi = () => api.get('/facebook/history');
export const getFacebookPreviewImageUrl = () => `${API_BASE}/api/facebook/preview-image?t=${Date.now()}`;

// Members (Turning Point)
export const listMembersApi = () => api.get('/members');
export const createMemberApi = (data) => api.post('/members', data);
export const updateMemberApi = (id, data) => api.patch(`/members/${id}`, data);
export const deleteMemberApi = (id) => api.delete(`/members/${id}`);
export const addMemberFromBookingApi = (bookingId) => api.post(`/members/from-booking/${bookingId}`);

export default api;
